<template>
    <LoadingScreen v-if="isLoading" :fetchData="fetchBookingDetail" class="z-50" />

    <div class="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-[1050]">
        <div
            class="bg-white w-full max-w-3xl lg:max-w-4xl p-4 sm:p-6 rounded-lg shadow-lg relative modal-wrapper overflow-auto max-h-[70vh]">
            <!-- Header -->
            <div class="flex justify-between items-center mb-4 border-b pb-2">
                <div class="flex flex-row gap-2">
                    <BaseBadge :status="bookingDetail?.Status" />
                    <h2 v-if="bookingCode.includes(`E`)" class="text-base sm:text-lg font-bold truncate">Order ID: {{ bookingCode || 'N/A' }}</h2>
                    <h2 v-else class="text-base sm:text-lg font-bold truncate">Order ID: {{ bookingDetail?.Code || 'N/A' }}</h2>
                </div>
                <BaseButton type="basic" class="text-gray-500 hover:text-red-500 transition" @click="$emit('close')">
                    ✕
                </BaseButton>
            </div>

            <!-- Payment Information -->
            <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 py-4 border-b">
                <div>
                    <p class="text-gray-600">
                        Metode Pembayaran: <strong> {{ paymentMethodText() }}</strong>
                    </p>
                    <p class="text-sm text-blue-500">Total: {{ $formatters.formatCurrency(bookingDetail?.Total || 0) }}
                    </p>
                </div>
                <div class="flex flex-row gap-4">
                <!-- <BaseButton
                    v-if="bookingDetail?.BookDetails && bookingDetail?.BookDetails.length > 1"
                    type="primary"
                    class="mt-4 sm:mt-0"
                    @click="actionShowExtendModal(bookingDetail?.BookDetails)"
                >
                    Extend All Room
                </BaseButton> -->
                <BaseButton v-if="bookingDetail?.Status == 2" type="primary" :disabled="bookingDetail?.Status !== 2"
                    class="mt-4 sm:mt-0" @click="handlePayment">
                    Pay Now
                </BaseButton>
                <BaseButton v-else type="primary" class="mt-4 sm:mt-0 flex flex-row gap-2"
                    @click.stop="handleDownloadReceipt(bookingDetail?.Code)">
                    <AppIcon icon="mdi:download" />
                    <p>{{ $t('button.downloadReceipt') }}</p>
                </BaseButton>
                </div>
            </div>

            <!-- Room Details -->
            <div v-if="bookingDetail?.BookDetails && bookingDetail?.BookDetails.length" class="space-y-6 pb-4">
                <div v-for="detail in bookingDetail.BookDetails" :key="detail.Id">
                    <div v-if="detail.ExtendType == 0" class="border rounded-lg p-4 shadow">
                        <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center">
                            <!-- Room Info -->
                            <div class="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                                <LazyImage :src="detail.Images?.[0]?.Filename || defaultImage"
                                    :alt="detail.Images?.[0]?.Filename || 'Room Image'"
                                    class="w-full sm:w-[100px] sm:h-[80px] rounded-lg object-cover" />
                                <div>
                                    <h3 class="text-lg font-semibold">{{ detail.Room?.Name || 'Room Name' }}</h3>
                                    <p class="text-gray-500">{{ detail.PropertyName || 'Property Name' }}</p>
                                    <!-- Price Details -->
                                    <div class="mt-4 space-y-2 text-sm">
                                        <div v-if="bookingDetail.Period == 1" class="flex justify-between gap-8">
                                            <span>{{ $formatters.formatCurrency(detail.PricePerNight) }} × {{
                                                detail.TotalNights }} nights</span>
                                            <span class="font-semibold">{{ $formatters.formatCurrency(detail.SubTotal)
                                                }}</span>
                                        </div>
                                        <div v-if="bookingDetail.Period == 2" class="flex justify-between gap-8">
                                            <span>{{ $formatters.formatCurrency(detail.SubTotal/detail.TotalMonths) }} × {{
                                                detail.TotalMonths }} months</span>
                                            <span class="font-semibold">{{ $formatters.formatCurrency(detail.SubTotal)
                                                }}</span>
                                        </div>
                                        <div v-if="detail.BookDetailAddOns?.length" class="space-y-1">
                                            <h5 class="text-gray-500 font-semibold">Add-ons</h5>
                                            <div v-for="addon in detail.BookDetailAddOns" :key="addon.Id"
                                                class="flex justify-between">
                                                <span>{{ addon.Name || addon.RoomAddOn?.AddOn?.Name || 'Add-On' }} × {{ addon.Quantity
                                                    }}</span>
                                                <span class="font-semibold">{{ $formatters.formatCurrency(addon.Total)
                                                    }}</span>
                                            </div>
                                        </div>

                                        <div class="flex flex-col sm:flex-row justify-star gap-4">

                                            <!-- Check-In -->
                                            <div class="flex flex-col sm:flex-row justify-between sm:items-center">
                                                <div>
                                                    <p class="text-gray-500">Check-In</p>
                                                    <p class="font-semibold text-gray-800">
                                                        {{ $formatters.formatLongDate(detail?.CheckIn) }}
                                                    </p>
                                                </div>
                                            </div>

                                            <!-- Check-Out -->
                                            <div class="flex flex-col sm:flex-row justify-between sm:items-center">
                                                <div>
                                                    <p class="text-gray-500">{{ detail?.CheckIn != detail?.OriginalCheckIn ? 'Extended Check-Out' : 'Check-Out'}}</p>
                                                    <p class="font-semibold text-gray-800">
                                                        {{ $formatters.formatLongDate(detail?.CheckOut) }}
                                                    </p>
                                                </div>
                                            </div>

                                        </div>

                                    </div>
                                </div>
                            </div>

                            <!-- PIN Display -->
                            <div v-if="detail?.Room?.TTLockId || detail?.Room?.HasMultiDoorlock">
                                <div v-if="bookingDetail?.Status !== 2 && !isHistoryOrder && isCheckoutValid(bookingDetail?.['CheckOutDate'], bookingDetail?.['CheckOutTime'])"
                                    class="flex flex-col items-start sm:items-end mt-4 sm:mt-0">
                                    <h4 class="text-gray-700 font-semibold mb-2">Room PIN</h4>
                                    <div v-if="detail.PinRooms?.[0]?.PIN" class="flex items-center gap-4">
                                        <p class="text-lg font-semibold tracking-widest flex items-center gap-2">
                                            {{ isPinVisible(detail.Id) ? detail.PinRooms?.[0]?.PIN+"#" : '••••••' }}
                                            <!-- Copy Icon -->
                                            <button v-if="isPinVisible(detail.Id)" @click="copyPin(detail.PinRooms?.[0]?.PIN)"
                                                class="focus:outline-none">
                                                <AppIcon icon="mdi:content-copy"
                                                    class="w-5 h-5 text-gray-600 hover:text-gray-800 transition" />
                                            </button>
                                            <!-- Eye Icon -->
                                            <button @click="togglePinVisibility(detail.Id, detail)" class="focus:outline-none">
                                                <AppIcon :icon="isPinVisible(detail.Id) ? 'mdi:eye-off' : 'mdi:eye'"
                                                    class="w-5 h-5 text-gray-600 hover:text-gray-800 transition" />
                                            </button>
                                        </p>
                                    </div>
                                    <button v-else
                                        @click="handleGenerateNewPinRoom(detail.Id)"
                                        class="bg-blue-500 text-white rounded px-4 py-2 hover:bg-blue-600 transition flex items-center gap-2">
                                        <AppIcon icon="mdi:key-wireless" />
                                        Generate PIN
                                    </button>
                                </div>
                            </div>
                        </div>

                        <!-- Footer Actions -->
                        <div v-if="bookingDetail?.Status !== 2 && !isHistoryOrder && isCheckoutValid(bookingDetail?.['CheckOutDate'], bookingDetail?.['CheckOutTime'])"
                            class="flex flex-col sm:flex-row justify-end gap-4 mt-6 border-t pt-4">
                            <BaseButton v-if="detail?.Room?.TTLockId || detail?.Room?.HasMultiDoorlock" type="primary" :disabled="!canGeneratePin(detail)"
                                @click="actionShowConfirmGeneratePinModal(detail)"
                                class="w-full sm:w-auto"
                                data-testid="generate-new-pin"
                                :data-room-id="detail?.RoomId"
                                :data-used="userGeneratedPinCount(detail)"
                                :data-max="maxGeneratePin(detail)">
                                Generate New Pin ( {{ userGeneratedPinCount(detail) }} / {{ maxGeneratePin(detail) }})
                            </BaseButton>


                            <BaseButton v-if="shouldShowCleaningButton(detail.Id, 'request')" type="primary"
                                @click="handleCleaningRequest(detail.Id, 'User Requested Cleaning')"
                                class="w-full sm:w-auto">
                                Request Cleaning
                            </BaseButton>
                            <BaseButton v-else-if="shouldShowCleaningButton(detail.Id, 'cancel')" type="basic"
                                class="text-red-500 w-full sm:w-auto" @click="handleCancelCleaningRequest(detail.Id)">
                                Cancel Cleaning
                            </BaseButton>


                            <BaseButton
                                v-if="detail?.IsAllowExtend == true"
                                type="primary"
                                :disabled="loadingInitExtend"
                                @click="actionShowExtendModal([detail])"
                                class="w-full sm:w-auto">
                                {{ loadingInitExtend ? 'Loading...' : `${isPendingExtend(detail) ? 'Pay Extend' : 'Extend'}` }}
                            </BaseButton>

                        </div>
                    </div>
                </div>
            </div>

            <!-- Guest Details -->
            <h4 class="text-gray-700 font-semibold pb-2 border-b my-2">Guest Details</h4>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm mb-4 my-2">
                <!-- Name -->
                <div>
                    <p class="text-gray-500">Name</p>
                    <p class="font-semibold text-gray-800">{{ bookingDetail?.Payment?.GuestName || 'N/A' }}</p>
                </div>

                <!-- Email -->
                <div>
                    <p class="text-gray-500">Email</p>
                    <p class="font-semibold text-gray-800">{{ bookingDetail?.Payment?.GuestEmail || 'N/A' }}</p>
                </div>

                <!-- Phone Number -->
                <div>
                    <p class="text-gray-500">Phone Number</p>
                    <p class="font-semibold text-gray-800">{{ bookingDetail?.Payment?.GuestPhoneNumber || 'N/A' }}</p>
                </div>
            </div>

            <!-- Review -->
            <h4 class="text-gray-700 font-semibold pb-2 border-b my-2">Review</h4>
            <div v-if="bookingDetail?.BookDetails?.length" class="pt-2 my-2 space-y-2">
                <div v-for="review in bookingDetail?.BookDetails" :key="review.Id">

                    <div v-if="review.ExtendType == 0 && review.UserRating" class="bg-white p-4 rounded-lg shadow mb-4">
                        <!-- RATING -->
                        <span class="font-semibold">{{ review.Room?.Name }}</span>
                        <div v-if="review.UserRating">
                            <div class="flex items-center mb-2">
                                <div class="flex items-center text-blue-500">
                                    <AppIcon v-for="i in 5" :key="i"
                                        :icon="i <= Math.floor(review.UserRating.Rating) ? 'mdi:star' : (i - review.UserRating.Rating < 1 && i > review.UserRating.Rating ? 'mdi:star-half-full' : 'mdi:star-outline')"
                                        :class="i <= review.UserRating.Rating ? 'text-blue-500' : 'text-blue-300'"
                                        class="w-4 h-4" />
                                </div>
                                <span class="text-gray-500 mx-2">•</span>
                                <span class="text-sm text-gray-500">{{ review.UserRating.Rating.toFixed(2) || '0.0' }}</span>
                            </div>
                            <p class="text-sm text-gray-700 mt-1">{{ review.UserRating.Description }}</p>

                            <div class="grid grid-cols-2 grid-rows-3 gap-4 pt-4">
                                <div v-for="ratingDetail in review.UserRating.UserRatingDetails" :key="ratingDetail.Id"
                                    class="flex items-center">
                                    <AppIcon :icon="ratingDetail.IconName" class="w-8 h-8 text-blue-500 mr-2" />
                                    <div class="flex flex-col">
                                        <span class="font-semibold">{{ ratingDetail.Name }}</span>
                                        <div class="flex items-center text-blue-500">
                                            <AppIcon v-for="i in 5" :key="i"
                                                :icon="i <= Math.floor(ratingDetail.Rating) ? 'mdi:star' : (i - ratingDetail.Rating < 1 && i > ratingDetail.Rating ? 'mdi:star-half-full' : 'mdi:star-outline')"
                                                :class="i <= ratingDetail.Rating ? 'text-blue-500' : 'text-blue-300'"
                                                class="w-4 h-4" />
                                            <!-- Display Rating Description -->
                                            <p class="text-sm mt-1 ml-4 text-gray-600">
                                                {{ ratingDescriptions[ratingDetail.Rating] || 'No description available' }}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div v-else>
                            <span class="font-medium">unavailable</span>
                        </div>

                    </div>

                </div>
            </div>
            <div v-else>
                <p>No reviews available.</p>
            </div>

            <!-- Price Details -->
            <h4 class="text-gray-700 font-semibold pb-2 border-b my-2">Price Details</h4>
            <div class="pt-2 my-2 space-y-2">
                <!-- Subtotal -->
                <div class="flex justify-between text-sm">
                    <span>Subtotal</span>
                    <span>{{ $formatters.formatCurrency(bookingDetail?.SubTotal || 0) }}</span>
                </div>

                <!-- Deposit -->
                <div class="flex justify-between text-sm">
                    <span>Deposit</span>
                    <span>
                        {{ $formatters.formatCurrency(
                            (bookingDetail?.BookDetails || [])
                            .flatMap(detail => detail.BookDetailDeposit || [])
                            .reduce((sum, charge) => sum + (charge?.Total || 0), 0)
                        ) }}
                    </span>
                </div>

                <!-- Service Fee -->
                <div class="flex justify-between text-sm">
                    <span>Other Charges</span>
                    <span>
                        {{ $formatters.formatCurrency(
                            (bookingDetail?.BookDetails || [])
                            .flatMap(detail => detail.BookDetailCharges?.filter(e=>!e?.IsPPN) || [])
                            .reduce((sum, charge) => sum + (charge?.Total || 0), 0)
                        ) }}
                    </span>
                    
                </div>

                <!-- Service Fee -->
                <div class="flex justify-between text-sm">
                    <span>PPN</span>

                    <span>
                        {{ $formatters.formatCurrency(
                            (bookingDetail?.BookDetails || [])
                            .flatMap(detail => detail.BookDetailCharges?.filter(e=>e?.IsPPN) || [])
                            .reduce((sum, charge) => sum + (charge?.Total || 0), 0)
                        ) }}
                    </span>
                    
                </div>


                <!-- Discount -->
                <div class="flex border-t justify-between text-sm">
                    <span class="mt-2">Discount</span>
                    <span class="mt-2">
                        - {{ $formatters.formatCurrency(bookingDetail?.Discount ?? 0) }}
                    </span>
                </div>


                <!-- Points -->
                <div class="flex justify-between text-sm">
                    <span>Points</span>
                    <span>- {{ $formatters.formatCurrency(bookingDetail?.Payment?.UsePoint || 0) }}</span>
                </div>

                <!-- Total -->
                <div class="flex justify-between text-sm font-semibold border-t pt-2">
                    <span>Total</span>
                    <span>{{ $formatters.formatCurrency(bookingDetail?.Total || 0) }}</span>
                </div>
            </div>
        </div>

        <transition name="fade">
            <div class="fixed inset-0 bg-black bg-opacity-25 z-index-9999 flex justify-center items-center"
                v-if="showConfirmGeneratePinModal">
                <div class="relative bg-white rounded-lg w-full max-w-xl p-6 mx-4">

                    <button @click="actionHideConfirmGeneratePinModal"
                        class="absolute top-8 right-6 text-gray-500 focus:outline-none" :disabled="isLoading">
                        <AppIcon icon="mdi:close" class="w-6 h-6" />
                    </button>

                    <h2 class="text-xl font-semibold mb-4">{{ $t('confirmGeneratePin') }}</h2>
                    <hr class="mb-4">

                    <p class="mt-4 text-md text-yellow-700">
                        {{ $t('confirmGeneratePinAlert') }}
                    </p>
                    <p class="mt-4 text-sm text-gray-700">
                        {{ maxGeneratePin(selectedDetail) - userGeneratedPinCount(selectedDetail) }} attempts remaining
                    </p>

                    <div class="flex flex-row justify-end gap-4">
                        <button type="submit" @click.prevent="actionHideConfirmGeneratePinModal"
                            class="py-2 px-4 mt-4 rounded-lg bg-red-500 text-white focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2">
                            <span>{{ $t('cancel') }}</span>
                        </button>
                        <button type="submit" @click.prevent="handleGenerateNewPinRoom(selectedDetail.Id)"
                            data-testid="generate-new-pin-confirm"
                            class="py-2 px-4 mt-4 rounded-lg bg-blue-600 text-white focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2" :disabled="isConfirming">
                            <span>{{ $t('continue') }}</span>
                        </button>
                    </div>
                </div>
            </div>
        </transition>

        <transition name="fade">
            <div class="fixed inset-0 bg-black bg-opacity-25 z-index-9999 flex justify-center items-center sm:items-center"
                v-if="showExtendModal">
                <!-- <div class="relative bg-white rounded-lg w-full max-w-xl p-6 mx-4 overflow-y-auto mt-12 sm:mt-0 max-h-[90vh]"> -->
                <div class="relative bg-white rounded-lg w-full max-w-xl p-6 mx-4 overflow-y-auto max-h-[80vh] mb-20">

                    <button @click="actionHideExtendModal"
                        class="absolute top-8 right-6 text-gray-500 focus:outline-none" :disabled="isLoading">
                        <AppIcon icon="mdi:close" class="w-6 h-6" />
                    </button>

                    <h2 class="text-xl font-semibold mb-4">Extend</h2>
                    <hr class="mb-4">

                    <div v-if="bookingDetail.Period == 1" class="flex flex-col gap-1 w-full">
                        <h2>New {{ $t('checkOut') }}</h2>
                        <BaseDatePicker 
                            iconBack="mdi:calendar" 
                            v-model="checkOutState" 
                            :checkInDate="checkInState"
                            class="w-full absolute z-100" 
                            :room-id="extendData?.RoomId"
                        />
                    </div>

                    <div v-if="bookingDetail.Period == 2" class="flex flex-col gap-1 mt-4">
                        <h2>{{ $t('propertyDetail.durationMonths') }}</h2>
                        <BaseSelect 
                            v-model="durationState" 
                            :options="durationOptions"
                            placeholder="Select duration" 
                        />
                    </div>

                    <div v-if="!errorInitExtend && (extendData?.Room?.TTLockId || extendData?.Room?.HasMultiDoorlock)" class="flex flex-row gap-2 mt-4">
                        <label v-for="option in pinOptions" :key="option.value" class="flex-row items-center" style="cursor: pointer;">
                            <input type="radio" v-model="generateNewPin" :value="option.value" />
                            {{ option.label }}
                        </label>
                    </div>

                    <div class="pt-4">
                        <label 
                          for="voucherCode" 
                          class="block text-md mb-1"
                        >
                          {{ $t('voucher.inputLabel')}}
                        </label>
                          
                        <div class="flex flex-row gap-2 items-center w-full">
                          <input id="voucherCode" type="text" v-model="voucherCode"
                            :placeholder="$t('voucher.inputPlaceholder')"
                            style="height: 40px;"
                            class="w-full p-3 border border-gray-300 rounded-lg" />
                          <button 
                            @click="applyVoucherExtendAction"
                            :disabled="voucherCode.length == 0 || isVoucherApplying"
                            class="text-white px-3 py-2 rounded-lg flex items-center gap-2"
                            :class="voucherCode.length == 0 ? 'bg-gray-500' : 'bg-blue-500'"
                          >
                            
                            <svg
                              v-if="isVoucherApplying"
                              class="w-4 h-4 animate-spin" 
                              fill="none" 
                              viewBox="0 0 24 24"
                              xmlns="http://www.w3.org/2000/svg">
                              <circle 
                                class="opacity-25" 
                                cx="12" 
                                cy="12" 
                                r="10"
                                stroke="currentColor"
                                stroke-width="4"
                              />
                              <path 
                                class="opacity-75" 
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                              />
                          </svg>
                            {{ $t('voucher.apply') }}
                          </button>
                        </div>
                    </div>

                    <div class="pt-4" v-if="extendData?.BookDetailExtendVouchers && extendData?.BookDetailExtendVouchers?.length > 0">
                        <label 
                          for="voucherCode" 
                          class="block text-sm mb-2"
                        >
                          {{ $t('voucher.currentVoucher')}}
                        </label>
                          
                        <div
                          v-for="(item, index) in extendData?.BookDetailExtendVouchers ?? []" :key="index"
                          class="flex justify-between items-center border rounded p-4 border-gray-300 py-2 mb-2"
                        >
                          <div class="flex items-center gap-2">
                            <AppIcon icon="mdi:ticket-percent-outline" class="h-6 w-6" />
                            <div class="flex flex-col gap-1 px-2">
                              <span class="font-semibold text-gray-700">{{ item.VoucherCode ?? '' }}</span>
                              <span class="text-sm text-blue-600">{{ $t('voucher.success', {
                                discount:
                                  $formatters.formatCurrency(item?.BookVoucher?.FinalDiscountAmount ?? 0)
                              }) }}</span>
                            </div>
                          </div>
                          <span class="text-gray-600 cursor-pointer" @click="removeVoucherExtendAction(item)">
                            <AppIcon icon="mdi:trash-can-outline" class="text-red-500" width="24" height="24" />
                          </span>
                        </div>

                    </div>

                    <div v-if="!errorInitExtend" class="mt-4">
                        <h2 class="mb-2">{{ $t('paymentMethod.choose') }}</h2>
                        <div v-for="method in getPaymentMethods.data" :key="method.Id">
                            <div 
                                class="flex items-center border rounded-lg px-4 p-2 cursor-pointer mb-2" 
                                :class="{
                                    'bg-blue-50 border-blue-500': selectedPaymentMethod?.Id == method.Id,
                                    'bg-white border-gray-300': selectedPaymentMethod?.Id != method.Id
                                }" 
                                @click="selectPaymentMethod(method)"
                            >
                                <LazyImage :src="method.Image?.FileName" :alt="method.Name" class="h-14 w-14 mr-4" />
                                <span class="font-semibold text-gray-700">{{ method.Name }}</span>
                            </div>
                        </div>
                    </div>

                    <div v-if="errorInitExtend">
                        <p class="mt-4 text-md text-red-700">
                            {{ errorInitExtend }}
                        </p>
                    </div>

                    <div v-if="!errorInitExtend">

                        <div class="flex flex-col gap-2 mt-4">

                            <div class="flex justify-between text-sm">
                                <span>Extended Check-out</span>
                                <span>{{ $formatters.formatLongDate(extendData?.CheckOut) }}</span>
                            </div>

                            <!-- Room Price -->
                            <div class="flex justify-between text-sm">
                                <span>Room Price</span>
                                <span>{{ $formatters.formatCurrency(extendData?.SubTotal || 0) }}</span>
                            </div>


                            <!-- Discounts -->
                            <div v-if="extendData?.BookDetailExtendVouchers">
                                <div v-for="(item, index) in extendData?.BookDetailExtendVouchers ?? []" :key="index">
                                    <div class="flex justify-between text-sm">
                                        <span>{{ item.VoucherCode }}</span>
                                        <span>-{{ $formatters.formatCurrency(item.BookVoucher?.FinalDiscountAmount || 0) }}</span>
                                    </div>
                                </div>
                            </div>

                            <!-- PPN -->
                            <div v-if="extendData?.BookDetailCharges">
                                <div v-for="(item, index) in extendData?.BookDetailCharges ?? []" :key="index">
                                    <div class="flex justify-between text-sm">
                                        <span>{{ item.Name }}</span>
                                        <span>{{ $formatters.formatCurrency(item.Total) }}</span>
                                    </div>
                                </div>
                            </div>

                            <!-- Total -->
                            <div class="flex justify-between text-sm font-semibold border-t pt-2">
                                <span>Total</span>
                                <span>{{ $formatters.formatCurrency(extendData?.Total || 0) }}</span>
                            </div>
                        </div>

                    </div>

                    <div class="flex flex-row justify-end gap-4 mt-4">

                        <BaseButton
                            type="danger"
                            @click="actionHideExtendModal"
                        >
                            Cancel
                        </BaseButton>

                        <BaseButton
                            type="primary"
                            :disabled="loadingCreateExtend || errorInitExtend"
                            @click="confirmCreateExtend">
                            {{ loadingCreateExtend ? 'Loading...' : 'Confirm' }}
                        </BaseButton>

                    </div>

                </div>
            </div>
        </transition>

        <transition name="fade">
            <div class="fixed inset-0 bg-black bg-opacity-25 z-index-9999 flex justify-center items-center"
                v-if="showPaymentExtend">
                <div class="relative bg-white rounded-lg w-full max-w-xl p-6 mx-4">

                    <button @click="actionHidePaymentExtendModal"
                        class="absolute top-8 right-6 text-gray-500 focus:outline-none" :disabled="isLoading">
                        <AppIcon icon="mdi:close" class="w-6 h-6" />
                    </button>

                    <h2 class="text-xl font-semibold mb-4">Payment Extend</h2>
                    <hr class="mb-4">

                    <p class="mt-4 text-md text-yellow-700">
                        Please complete your payment!
                    </p>

                    <p class="text-md" :class="{'text-red-600 font-bold': isExpired, 'text-red-500': !isExpired}">
                        {{ expiryText }}
                    </p>

                    <div class="flex flex-col gap-2 mt-4">

                        <div 
                            v-if="extendPaymentData?.VANumber" 
                            class="flex flex-col items-start gap-2 text-sm bg-white p-3 rounded-xl shadow-sm"
                        >
                            <!-- Nama metode pembayaran -->
                            <span class="font-medium text-gray-700">
                                {{ extendPaymentData?.PaymentDetails[0]?.PaymentMethodName }}
                            </span>

                            <!-- VA Number -->
                            <span class="font-mono text-gray-900 text-lg tracking-wide">
                                {{ extendPaymentData?.VANumber }}
                            </span>

                            <!-- Tombol copy -->
                            <button 
                                v-if="!isExpired" 
                                @click="handleCopy"
                                :class="{
                                'bg-green-50 text-green-600 border-green-300': isCopied, 
                                'bg-blue-50 text-blue-600 border-blue-300': !isCopied 
                                }"
                                class="copy-button px-3 py-1 border rounded-lg flex items-center gap-2 transition-all duration-300 ease-in-out hover:scale-105"
                            >
                                <AppIcon 
                                :icon="isCopied ? 'mdi:check' : 'mdi:content-copy'"
                                class="w-5 h-5 transition-transform duration-300"
                                :class="isCopied ? 'text-green-500 scale-110' : 'text-blue-500'" 
                                />
                                <span class="transition-opacity duration-300 ease-in-out">
                                {{ isCopied ? "Copied!" : "Copy" }}
                                </span>
                            </button>
                        </div>


                        <div v-if="extendPaymentData?.XenditUrl" class="flex justify-between text-sm">
                            <span>Payment Method</span>
                            <span>{{ extendPaymentData?.PaymentDetails[0]?.PaymentMethodName }}</span>
                        </div>

                        <div class="flex justify-between text-sm">
                            <span>Extended Check-out</span>
                            <span>{{ $formatters.formatLongDate(extendData?.CheckOut) }}</span>
                        </div>

                        <div class="flex justify-between text-sm">
                            <span>Room Price</span>
                            <span>{{ $formatters.formatCurrency(extendData?.SubTotal || 0) }}</span>
                        </div>

                        <!-- PPN -->
                        <div v-if="extendData?.BookDetailCharges">
                            <div v-for="(item, index) in extendData?.BookDetailCharges ?? []" :key="index">
                                <div class="flex justify-between text-sm mt-2">
                                    <span>{{ item.Name }}</span>
                                    <span>{{ $formatters.formatCurrency(item.Total) }}</span>
                                </div>
                            </div>
                        </div>

                        <!-- Total -->
                        <div class="flex justify-between text-sm font-semibold border-t pt-2">
                            <span>Total</span>
                            <span>{{ $formatters.formatCurrency(extendData?.Total || 0) }}</span>
                        </div>
                    </div>

                    <div class="flex flex-row justify-end gap-4">
                        <button @click="actionCancelExtend" type="submit"
                            class="py-2 px-4 mt-4 rounded-lg text-red focus:outline-none focus:ring-2 focus:ring-red-600 focus:ring-offset-2" :disabled="loadingCancelExtend">
                            <span>Cancel Extend</span>
                        </button>
                        <button v-if="true" type="submit" @click="actionCheckPaymentStatus"
                            class="py-2 px-4 mt-4 rounded-lg bg-blue-600 text-white focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2" :disabled="loadingCheckPaymentStatus">
                            <span>Check Payment Status</span>
                        </button>
                        <button v-if="extendPaymentData?.XenditUrl" @click="handlePaymentExtend" type="submit"
                            class="py-2 px-4 mt-4 rounded-lg bg-blue-600 text-white focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2">
                            <span>Pay Now</span>
                        </button>
                    </div>
                </div>
            </div>
        </transition>

        <transition name="fade">
            <div class="fixed inset-0 bg-black bg-opacity-25 z-index-9999 flex justify-center items-center"
                v-if="showConfirmExtendOTAModal">
                <div class="relative bg-white rounded-lg w-full max-w-xl p-6 mx-4">

                    <button @click="actionHideConfirmExtendOTAModal"
                        class="absolute top-8 right-6 text-gray-500 focus:outline-none" :disabled="isLoading">
                        <AppIcon icon="mdi:close" class="w-6 h-6" />
                    </button>

                    <h2 class="text-xl font-semibold mb-4">Extend from OTA</h2>
                    <hr class="mb-4">

                    <p class="mt-4 text-md text-green-700">
                        Data found for extend Check-out until {{ $formatters.formatDate(extendOTAData?.DTEND) }}, Continue?
                    </p>

                    <div class="flex flex-row justify-end gap-4 mt-4">
                        <BaseButton
                            type="danger"
                            @click="actionHideConfirmExtendOTAModal">
                            {{ $t('cancel') }}
                        </BaseButton>
                        <BaseButton
                            type="primary"
                            :disabled="loadingConfirmExtendOTA"
                            @click="actionConfirmExtendOTA(false)">
                            {{ loadingConfirmExtendOTA ? 'Loading...' : `${$t('continue')} with existing PIN` }}
                        </BaseButton>
                        <BaseButton
                            type="primary"
                            :disabled="loadingConfirmExtendOTA"
                            @click="actionConfirmExtendOTA(true)">
                            {{ loadingConfirmExtendOTA ? 'Loading...' : `${$t('continue')} with new PIN` }}
                        </BaseButton>
                    </div>
                </div>
            </div>
        </transition>

        <transition name="fade">
            <div class="fixed inset-0 bg-black bg-opacity-25 z-index-9999 flex justify-center items-center sm:items-center"
                v-if="showDownloadInvoiceModal">
                <div class="relative bg-white rounded-lg w-full max-w-xl p-6 mx-4 overflow-y-auto mt-12 sm:mt-0 max-h-[80vh]">

                    <button @click="showDownloadInvoiceModal = false"
                        class="absolute top-8 right-6 text-gray-500 focus:outline-none" :disabled="isLoading">
                        <AppIcon icon="mdi:close" class="w-6 h-6" />
                    </button>

                    <h2 class="text-xl font-semibold mb-4">Download Invoice</h2>
                    
                    <div v-for="invoice in invoices" :key="invoice.Id">
                        <div 
                            class="flex items-center border rounded-lg px-4 p-2 cursor-pointer mb-2" 
                            :class="{
                                'bg-blue-50 border-blue-500': selectedInvoice?.BookingCode == invoice.BookingCode,
                                'bg-white border-gray-300': selectedInvoice?.BookingCode != invoice.BookingCode
                            }" 
                            @click="selectedInvoice = invoice"
                        >
                            <div class="flex flex-col">
                            <span class="font-semibold text-gray-700">{{ invoice.BookingCode }}</span>
                            <span class="text-sm text-gray-500">{{ $formatters.formatCurrency(invoice.Total) }}</span>
                            <span class="text-sm text-gray-500">{{ $formatters.formatLongDate(invoice.PaidAt) }}</span>
                            </div>
                        </div>
                    </div>

                    <div class="flex flex-row justify-end gap-4 mt-4">

                        <BaseButton
                            type="danger"
                            @click="showDownloadInvoiceModal = false"
                        >
                            Cancel
                        </BaseButton>

                        <BaseButton
                            type="primary"
                            :disabled="!selectedInvoice"
                            @click="downloadSelectedInvoice">
                            Download
                        </BaseButton>

                    </div>

                </div>
            </div>
        </transition>

    </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { getResponseMessage } from "@/utils/responseMapping";
// Alias supaya jelas method di bawah cuma meneruskan ke helper bersama, bukan memanggil dirinya.
import { userGeneratedPinCount as pinCountOf, maxGeneratePin as pinMaxOf, canGeneratePin as pinCanGenerate } from "@/utils/pinQuota";
import moment from "moment";

export default {
    props: {
        bookingCode: {
            type: String,
            required: true,
        },
        isHistoryOrder: {
            type: Boolean,
            required: false,
            default: false,
        }
    },
    data() {
        return {
            bookingDetail: null,
            isLoading: true,
            defaultImage: "https://via.placeholder.com/200", // Default jika gambar tidak tersedia
            visiblePins: {}, // State untuk melacak PIN yang sedang terlihat
            isCleaning: [], // State untuk melacak status cleaning room per room ID
            queryKey: null, // Simpan key dari query params
            ratingDescriptions: {
                1: this.$t('propertyDetail.veryPoor') || 'Sangat Buruk',
                2: this.$t('propertyDetail.poor') || 'Buruk',
                3: this.$t('propertyDetail.average') || 'Cukup',
                4: this.$t('propertyDetail.good') || 'Baik',
                5: this.$t('propertyDetail.excellent') || 'Sangat Baik',
            },
            reqCleanLoading: false,
            showConfirmGeneratePinModal: false,
            selectedDetail: null,
            showExtendModal: false,
            extendBookDetails: [],
            checkInState: null,
            checkOutState: null,
            pinOptions: [
                {
                    label: 'Use exist pin',
                    value: false,
                },
                {
                    label: 'Generate new pin',
                    value: true,
                },
            ],
            generateNewPin: false,
            selectedPaymentMethod: null,
            durationOptions: Array.from({ length: 12 }, (_, i) => {
                return {
                    value: i + 1,
                    label: `${i + 1} ${this.$t('months')}`
                };
            }),
            durationState: 0,
            loadingInitExtend: false,
            errorInitExtend: null,
            extendData: null,
            loadingCreateExtend: false,
            showPaymentExtend: false,
            loadingCancelExtend: false,
            isCopied: false,
            loadingCheckPaymentStatus: false,
            showConfirmExtendOTAModal: false,
            loadingConfirmExtendOTA: false,
            extendOTAData: null,
            voucherCode: '',
            isVoucherApplying: false,
            showDownloadInvoiceModal: false,
            invoices: [],
            selectedInvoice: null,
        };
    },
    watch: {
        checkOutState: {
            handler(newVal) {
                if (newVal) {
                    this.initExtendAction();
                }
            },
            immediate: true
        },
        durationState: {
            handler(newVal) {
                if (newVal) {
                    this.initExtendAction();
                }
            },
            immediate: true
        }
    },
    computed: {
        ...mapGetters("payment", ["getPaymentMethods"]),
        calculateExtendTotal() {
            let total = 0;
            if (this.extendData?.BookDetailCharges) {
                this.extendData.BookDetailCharges.forEach(item => {
                    total += item.Total;
                })
            }
            return total + (this.extendData?.Total ?? 0);
        },
        extendPaymentData() {
            return this.bookingDetail?.ExtendPayments?.find(e => e.RoomId == this.extendData?.RoomId && e.ExtendType == 2) ?? null;
        },
        isExpired() {
            if (!this.extendPaymentData?.ExpiredOn) return false;
            const expiry = new Date(this.extendPaymentData.ExpiredOn);
            return expiry < new Date();
        },
        expiryText() {
            if (!this.extendPaymentData?.ExpiredOn) return 'No Expiry';
            
            const expiry = new Date(this.extendPaymentData.ExpiredOn);
            const now = new Date();
            
            if (expiry < now) {
                return 'EXPIRED';
            }
            
            const diffMs = expiry - now;
            const diffMins = Math.ceil(diffMs / (1000 * 60));
            
            return `Expired in ${diffMins} minutes`;
        }
    },
    methods: {
        ...mapActions("booking",
            [
                "fetchOneBooking",
                "generatePin",
                "fetchReceipt",

                //Cleaning Actions
                "cleaningRoomRequest",
                "cancelCleaningRoomRequest",
                "fetchCleaningRequests",

                //Regenerate new pin
                "generateNewPinRoom",

                "downloadReceipt",

                "initExtendData",
                "createExtend",
                "cancelExtend",
                "confirmExtendOTA",
                "applyVoucherExtend",
                "removeVoucherExtend",
            ]),
        ...mapActions("payment", ["processCheckout", "fetchPaymentMethods"]),

        paymentMethodText() {
            // Backend sudah mengirim label baku `Nama Metode (CHANNEL)`; channel baru ada setelah dibayar,
            // jadi saat menunggu pembayaran otomatis tinggal nama metodenya.
            return this.bookingDetail?.Payment?.PaymentDetails?.[0]?.PaymentMethodName?.trim() || 'N/A';
        },

        async handleDownloadReceipt(bookingCode) {
            // if (this.bookingDetail?.ExtendPayments && this.bookingDetail.ExtendPayments?.length > 0) {
            //     this.showDownloadInvoiceModal = true;
            //     return;
            // }
            try {
                console.log("bookinCode", bookingCode);
                if (!bookingCode || typeof bookingCode !== "string") {
                    console.error("Invalid booking code:", bookingCode);
                    this.$alert.toast("error", "Booking code is invalid.");
                    return;
                }

                const params = this.queryKey ? { key: this.queryKey } : {};
                let code = bookingCode;
                if (this.bookingCode.includes("E")) {
                    code = this.bookingCode;
                }

                // Panggil action downloadReceipt dari Vuex
                await this.downloadReceipt({ bookingCode: code, params });
            } catch (error) {
                console.error("Failed to download receipt:", error);
                this.$alert.toast("error", this.$t("alert.failedToFetchReceipt"));
            }
        },

        async handleCleaningRequest(roomId, description) {
            if (this.reqCleanLoading) {
                console.log('still requesting, return')
                return;
            }
            const payload = {
                BookDetailId: roomId,
                Description: description || "Standard Cleaning Request",
            };

            console.log("payload", payload);

            try {
                this.reqCleanLoading = true;
                const response = await this.cleaningRoomRequest(payload);
                // Perbarui status cleaning untuk ID tertentu
                if (!this.isCleaning[roomId]) {
                    this.isCleaning[roomId] = {};
                }

                await this.fetchBookingDetail();

                console.log("Cleaning Room Requested:", response);
                this.isCleaning[roomId] = response.data.data; // Update status cleaning sesuai room ID
                const successMessage = getResponseMessage(response.status, response.data.code);
                this.$alert.toast("success", successMessage);
                this.reqCleanLoading = false;
            } catch (error) {
                console.log("error", error);
                const errorCode = error.response?.data?.code;
                const errorStatus = error.response?.status;

                // Tampilkan pesan error berdasarkan kode dan status
                const errorMessage = getResponseMessage(errorStatus, errorCode);
                this.$alert.toast("error", errorMessage);
                this.reqCleanLoading = false;
            }
        },

        async handleCancelCleaningRequest(roomId) {
            if (this.reqCleanLoading) {
                console.log('still cancel requesting, return')
                return;
            }
            try {
                this.reqCleanLoading = true;
                const response = await this.cancelCleaningRoomRequest(roomId);
                console.log("Cleaning Request Canceled:", response);
                this.isCleaning[roomId] = response.data.data; // Update status cleaning sesuai room ID

                await this.fetchBookingDetail();

                const successMessage = getResponseMessage(response.status, response.data.code);
                this.$alert.toast("success", successMessage);
                this.reqCleanLoading = false;
            } catch (error) {
                const errorCode = error.response?.data?.code;
                const errorStatus = error.response?.status;

                // Tampilkan pesan error berdasarkan kode dan status
                const errorMessage = getResponseMessage(errorStatus, errorCode);
                this.$alert.toast("error", errorMessage);
                this.reqCleanLoading = false;
            }
        },

        async loadCleaningRequests(roomId) {
            try {
                const response = await this.fetchCleaningRequests(roomId);

                if (response.data && response.data.length > 0) {
                    // Proses setiap item dalam response
                    response.data.forEach((item) => {
                        // Cari book detail berdasarkan BookDetailId
                        const bookDetail = this.bookingDetail.BookDetails.find(
                            (detail) => detail.Id === item.BookDetailId
                        );

                        if (bookDetail) {
                            // Gabungkan semua CleaningRequestStatuses ke dalam CleaningRequests
                            if (!bookDetail.CleaningRequests) {
                                bookDetail.CleaningRequests = [];
                            }

                            // Tambahkan data baru ke CleaningRequests tanpa overwrite
                            item.CleaningRequestStatuses.forEach((status) => {
                                if (
                                    !bookDetail.CleaningRequests.some(
                                        (existingStatus) => existingStatus.Id === status.Id
                                    )
                                ) {
                                    bookDetail.CleaningRequests.push(status);
                                }
                            });

                            // Tentukan status terakhir berdasarkan CreatedOn
                            bookDetail.LatestCleaningStatus = bookDetail.CleaningRequests.reduce(
                                (latest, current) =>
                                    new Date(latest.CreatedOn) > new Date(current.CreatedOn)
                                        ? latest
                                        : current,
                                bookDetail.CleaningRequests[0]
                            );
                        }
                    });
                } else {
                    // Jika response kosong, set CleaningRequests sebagai array kosong untuk roomId
                    const bookDetail = this.bookingDetail.BookDetails.find(
                        (detail) => detail.Id === roomId
                    );

                    if (bookDetail) {
                        bookDetail.CleaningRequests = [];
                        bookDetail.LatestCleaningStatus = null;
                    }
                }

                console.log("Updated bookingDetail.BookDetails:", this.bookingDetail.BookDetails);
            } catch (error) {
                console.error("Error loading cleaning requests:", error);
            }
        },

        shouldShowCleaningButton(bookDetailId, type) {
            // Temukan BookDetail berdasarkan ID
            const bookDetail = this.bookingDetail?.BookDetails?.find(detail => detail.Id === bookDetailId);

            if (!bookDetail || !bookDetail.CleaningRequests || bookDetail.CleaningRequests.length === 0) {
                // Jika tidak ada CleaningRequests, hanya tampilkan tombol "Request Cleaning"
                return type === "request";
            }

            // Ambil status terakhir dari CleaningRequests
            const latestStatus = bookDetail.CleaningRequests.reduce((latest, current) => {
                return new Date(latest.CreatedOn) > new Date(current.CreatedOn) ? latest : current;
            }, bookDetail.CleaningRequests[0]);

            if (type === "request") {
                // Logika untuk "Request Cleaning"
                return (
                    !latestStatus || // Tidak ada status
                    latestStatus.Status === null || // Status null
                    latestStatus.Status === 4 || // Cleaning selesai
                    latestStatus.Status === 3 // Cleaning expired
                );
            } else if (type === "cancel") {
                // Logika untuk "Cancel Cleaning"
                return latestStatus.Status === 1 || latestStatus.Status === 2; // Cleaning sedang berjalan atau diproses
            }

            return false; // Default
        },

        getCleaningStatus(bookDetailId) {
            return this.isCleaning.find(item => item.BookDetailId === bookDetailId) || { Status: null };
        },

        // Delegasi ke utils/pinQuota — jangan hitung ulang di sini (lihat catatan di file itu).
        userGeneratedPinCount(detail) {
            return pinCountOf(detail);
        },
        maxGeneratePin(detail) {
            return pinMaxOf(detail);
        },
        canGeneratePin(detail) {
            return pinCanGenerate(detail);
        },
        async handleGenerateNewPinRoom(bookingDetailId) {
            if (this.isLoading) {
                console.log('still generating pin, return');
                return;
            }
            try {
                this.isLoading = true;

                const response = await this.generateNewPinRoom(bookingDetailId);
                console.log("Generated PIN:", response);

                // Reload booking details untuk memperbarui data
                await this.fetchBookingDetail();

                this.$alert.toast("success", "PIN generated successfully!");


            } catch (error) {
                console.error("Failed to generate PIN:", error);
                this.$alert.toast("error", error?.response?.data?.msg ?? "Failed to generate PIN. Please try again.");
            } finally {
                this.isLoading = false;
                this.actionHideConfirmGeneratePinModal();
            }
        },

        async fetchBookingDetail() {
            try {
                this.isLoading = true;
                const params = this.queryKey ? { key: this.queryKey } : {};
                const data = await this.fetchOneBooking({
                    bookingCode: this.bookingCode,
                    params,
                });

                // Tambahkan properti showPin ke setiap detail
                if (data.data.BookDetails) {
                    data.data.BookDetails = data.data.BookDetails.map(detail => ({
                        ...detail,
                        showPin: false, // Default showPin adalah false
                    }));
                }

                this.bookingDetail = data.data; // Simpan data booking

                // await this.fetchArrayCleaningRequestsTemp();

                if (this.bookingDetail) {
                    this.invoices = [];
                    if (this.bookingDetail.ExtendPayments) {
                        this.invoices.push({
                            BookingCode: this.bookingDetail?.Code,
                            Total: this.bookingDetail?.Payment?.Total ?? 0,
                            PaidAt: this.bookingDetail?.Payment?.PaidAt,
                        });
                        let i = 1;
                        for (const p of this.bookingDetail.ExtendPayments) {
                        this.invoices.push({
                            BookingCode: `${this.bookingDetail?.Code}E${i}`,
                            Total: p.Total ?? 0,
                            PaidAt: p.PaidAt,
                        });
                        i++;
                        }
                    }
                    }

            } catch (error) {
                // console.error("Fetch Booking Error:", error.response?.data || error.message);
            } finally {
                this.isLoading = false;
            }
        },
        isCheckoutValid(checkOutDate, checkOutTime) {
            // console.log("checkoutDate", checkOutDate);
            // Pastikan input tidak null atau undefined
            if (!checkOutDate || !checkOutTime) return false;

            // Gabungkan tanggal dan waktu checkout secara eksplisit
            const checkoutDateTime = new Date(`${checkOutDate}T${checkOutTime}`);

            // Ambil waktu sekarang dalam zona waktu lokal
            const currentDateTime = new Date();

            // Debug: Log untuk melihat nilai yang dibandingkan
            // console.log("Checkout DateTime:", checkoutDateTime.toISOString());
            // console.log("Current DateTime:", currentDateTime.toISOString());

            // Pastikan hasil perbandingan antara waktu sekarang dan waktu checkout
            return currentDateTime <= checkoutDateTime;
        },
        handlePayment() {
            if (this.bookingDetail?.Payment?.VANumber) {
                const route = this.$router.resolve({ 
                    path: `/pay`,
                    query: { invoiceNo: this.bookingCode }
                });
                window.open(route.href, "_blank");
            }
            else if (this.bookingDetail?.Payment?.XenditUrl) {
                window.open(this.bookingDetail.Payment.XenditUrl, "_blank");
            }
        },
        handlePaymentExtend() {
            // if (this.bookingDetail?.Payment?.VANumber) {
            //     const route = this.$router.resolve({ 
            //         path: `/pay`,
            //         query: { invoiceNo: this.bookingCode }
            //     });
            //     window.open(route.href, "_blank");
            //     return;
            // }
            if (this.extendPaymentData?.XenditUrl) {
                window.open(this.extendPaymentData.XenditUrl, "_blank");
                return;
            }
        },
        togglePinVisibility(id, detail) {
            // Periksa apakah PIN tersedia
            if (!detail.PinRooms?.[0]?.PIN) {
                // Jika PIN belum ada, panggil handleGeneratePin
                console.log("PIN not available. Generating...");
                this.handleGeneratePin(id);
                return; // Keluar dari fungsi setelah generate PIN
            }

            // Jika PIN tersedia, toggle visibilitas
            if (this.visiblePins[id]) {
                delete this.visiblePins[id]; // Hapus properti jika PIN sudah terlihat
            } else {
                this.visiblePins[id] = true; // Tambahkan properti baru untuk menunjukkan PIN terlihat
            }
        },
        isPinVisible(id) {
            // Periksa apakah PIN terlihat berdasarkan ID
            return !!this.visiblePins[id];
        },
        copyPin(pin) {
            if (pin) {
                navigator.clipboard.writeText(`${pin}#`)
                    .then(() => {
                        console.log("PIN copied to clipboard!");
                        this.$alert.toast('success', this.$t('alert.pinCopied'));
                        // this.$alert.toast('warning', this.$t('alert.checkoutFailed'))
                    })
                    .catch(err => {
                        console.error("Failed to copy PIN:", err);
                    });
            }
        },
        async handleGeneratePin(bookingDetailId) {
            try {
                await this.generatePin(bookingDetailId);

                await this.fetchBookingDetail();

            } catch (error) {
                console.error("Failed to generate PIN:", error);
                // this.$toast.error("Failed to generate PIN. Please try again.");
            }
        },

        async fetchArrayCleaningRequestsTemp() {
            // Jangan panggil loadCleaningRequests jika queryKey ada
            if (!this.queryKey && this.bookingDetail?.BookDetails?.length) {
                for (const detail of this.bookingDetail.BookDetails) {
                    await this.loadCleaningRequests(detail.Id);
                }
            }
        },
        actionShowConfirmGeneratePinModal(detail) {

            if (moment(detail?.CheckIn).isAfter(moment())) {
                this.$alert.toast('warning', 'Cannot generate before check-in');
                return;
            }
            this.selectedDetail = detail;
            this.showConfirmGeneratePinModal = true;
        },
        actionHideConfirmGeneratePinModal() {
            this.selectedDetail = null;
            this.showConfirmGeneratePinModal = false;
            this.selectedPaymentMethod = null;
            this.generateNewPin = false;
            this.checkInState = null;
            this.checkOutState = null;

        },
        async actionShowExtendModal(bookDetails) {

            if (this.isPendingExtend(bookDetails[0])) {
                this.extendData = this.bookingDetail.BookDetails.find(e => e.RoomId == bookDetails[0].RoomId && e.ExtendType != 0);
                this.$alert.toast('warning', 'You have pending extend request');
                this.actionShowPaymentExtendModal();
                return;
            }

            if (bookDetails && bookDetails.length > 0) {
                this.extendBookDetails = bookDetails;
                if (this.bookingDetail?.Period == 2) {
                    if (this.durationState == 1) {
                        this.initExtendAction();
                    } else {
                        this.durationState = 1;
                    }
                } else {
                    this.checkInState = new Date(bookDetails[0].CheckOut);
                    const tempCheckOut = new Date(this.checkInState);
                    tempCheckOut.setDate(this.checkInState.getDate() + 1);
                    this.checkOutState = tempCheckOut;
                }
            }

        },
        async initExtendAction() {

            await this.loadPaymentMethods();

            const req = {
                BookingCode: this.bookingDetail?.Code ?? null,
                BookDetailId: this.extendBookDetails[0]?.Id ?? null,
                PeriodType: this.bookingDetail?.Period,
                CheckOut: this.toLocalDateString(this.checkOutState),
                Months: this.durationState,
            };

            this.loadingInitExtend = true;
            this.errorInitExtend = null;
            try {
                const temp = await this.initExtendData(req);
                if (temp.code == 0) {
                    if (temp.data) {
                        this.extendData = temp.data;
                        this.showExtendModal = true;
                        if (this.getPaymentMethods?.data.length > 0) {
                            this.selectPaymentMethod(this.getPaymentMethods.data[0]);
                        }
                    }
                }
                else if (temp.code == 102) {
                    if (temp.data) {
                        this.extendOTAData = temp.data;
                        this.actionShowConfirmExtendOTAModal();
                    }
                }
            } catch (error) {
                console.error("Gagal memuat data extend:", error);
                this.errorInitExtend = error.response?.data?.msg || "Failed to init extend, please try again";
                this.$alert.toast('error', this.errorInitExtend);
            } finally {
                this.loadingInitExtend = false;
            }

        },
        actionHideExtendModal() {
            if (this.loadingCreateExtend) return;
            this.showExtendModal = false;
            this.durationState = 0;
            this.checkOutState = null;
        },
        async loadPaymentMethods() {
            try {
                await this.fetchPaymentMethods(this.bookingDetail?.OwnerId);
            } catch (error) {
                console.error("Gagal memuat metode pembayaran:", error);
            }
        },
        selectPaymentMethod(method) {
            this.selectedPaymentMethod = method;
        },
        async confirmCreateExtend() {
            if (this.loadingCreateExtend) return;
            this.loadingCreateExtend = true;

            try {

                const req = {
                    BookingCode: this.bookingDetail?.Code ?? null,
                    BookDetailId: this.extendData?.Id ?? null,
                    PaymentMethodId: this.selectedPaymentMethod?.Id ?? null,
                    PeriodType: this.bookingDetail.Period,
                    CheckOut: this.toLocalDateString(this.checkOutState),
                    Months: this.durationState,
                    GenerateNewPIN: this.generateNewPin,
                };

                const res = await this.createExtend(req);
                if (res.data) {
                    this.bookingDetail = res.data;
                    this.extendData = this.bookingDetail.BookDetails.find(e => e.RoomId == this.extendBookDetails[0].RoomId && e.ExtendType != 0);
                }
                this.$alert.toast('success', 'Please complete extend payment');
                this.loadingCreateExtend = false;
                this.actionHideExtendModal();
                this.actionShowPaymentExtendModal();
            } catch (error) {
                this.loadingCreateExtend = false;
                console.error("Gagal membuat extend:", error);
                this.$alert.toast('error', error.response?.data?.msg ?? "Extend failed, please try again!");
            }
        },
        actionHidePaymentExtendModal() {
            this.showPaymentExtend = false;
        },
        actionShowPaymentExtendModal() {

            this.showPaymentExtend = true;
            if (this.extendPaymentData?.ExpiredOn) {
                const expiredOn = new Date(this.extendPaymentData.ExpiredOn);
                expiredOn.setSeconds(expiredOn.getSeconds() + 30);
                const now = new Date();
                const timeUntilExpiry = expiredOn.getTime() - now.getTime();

                if (timeUntilExpiry > 0) {
                    setTimeout(async () => {
                        await this.fetchBookingDetail();
                        this.actionHidePaymentExtendModal();
                    }, timeUntilExpiry);
                }
            }
        },
        toLocalDateString(dateObj) {
            // Pastikan dateObj adalah Date valid
            if (!(dateObj instanceof Date) || isNaN(dateObj)) {
                return null; // atau kembalikan string kosong ""
            }

            const year = dateObj.getFullYear();
            // getMonth() dimulai dari 0, jadi tambahkan +1
            const month = String(dateObj.getMonth() + 1).padStart(2, '0');
            const day = String(dateObj.getDate()).padStart(2, '0');

            // Return format YYYY-MM-DD
            return `${year}-${month}-${day}`;
        },
        isPendingExtend(bookingDetail) {
            return this.bookingDetail?.BookDetails?.find(e => e.RoomId == bookingDetail.RoomId && e.ExtendType == 2) ?? null;
        },
        async actionCancelExtend() {
            if (this.loadingCancelExtend) return;
            this.loadingCancelExtend = true;
            try {
                const req = {
                    BookingCode: this.bookingDetail?.Code ?? null,
                    BookDetailId: this.extendData?.Id ?? null,
                };
                const res = await this.cancelExtend(req);
                if (res.data) {
                    this.bookingDetail = res.data;
                }
                this.$alert.toast('success', 'Extend cancelled');
                this.loadingCancelExtend = false;
                this.actionHidePaymentExtendModal();
            } catch (error) {
                this.loadingCancelExtend = false;
                console.error("Gagal membatalkan extend:", error);
            }
        },
        handleCopy() {
            this.copyToClipboard(this.extendPaymentData?.VANumber); // Salin ke clipboard
            this.isCopied = true; // Tampilkan status copied
            setTimeout(() => {
                this.isCopied = false; // Kembalikan ke tombol 'Copy' setelah 2 detik
            }, 1000);
        },
        copyToClipboard(text) {
            // Cek apakah `navigator.clipboard` tersedia
            if (navigator.clipboard && navigator.clipboard.writeText) {
                navigator.clipboard.writeText(text).catch((err) => {
                    console.error("Error copying text: ", err);
                });
            } else {
                // Fallback untuk browser yang tidak mendukung clipboard API
                const textarea = document.createElement("textarea");
                textarea.value = text;
                textarea.style.position = "fixed";  // Hindari scroll saat menyalin
                document.body.appendChild(textarea);
                textarea.focus();
                textarea.select();
                try {
                    document.execCommand("copy");
                } catch (err) {
                    console.error("Fallback: Copy command failed", err);
                }
                document.body.removeChild(textarea);
            }
        },
        async actionCheckPaymentStatus() {
            this.showPaymentExtend = false;
            if (this.loadingCheckPaymentStatus) return;
            this.loadingCheckPaymentStatus = true;
            try {
                await this.fetchBookingDetail()
                // this.actionShowPaymentExtendModal()
                this.extendData = this.bookingDetail.BookDetails.find(e => e.RoomId == this.extendBookDetails[0].RoomId && e.ExtendType != 0);
                this.$alert.toast('success', 'Payment status checked');
                this.loadingCheckPaymentStatus = false;
            } catch (error) {
                this.loadingCheckPaymentStatus = false;
            }
        },
        actionShowConfirmExtendOTAModal() {
            this.showConfirmExtendOTAModal = true;
        },
        actionHideConfirmExtendOTAModal() {
            this.showConfirmExtendOTAModal = false;
        },
        async actionConfirmExtendOTA(generateNewPin) {
            if (this.loadingConfirmExtendOTA) return;
            this.loadingConfirmExtendOTA = true;
            try {
                const req = {
                    BookingCode: this.bookingDetail?.Code ?? null,
                    BookDetailId: this.extendBookDetails[0]?.Id ?? null,
                    GenerateNewPIN: generateNewPin,
                };
                const res = await this.confirmExtendOTA(req);
                if (res.data) {
                    this.bookingDetail = res.data;
                }
                this.$alert.toast('success', 'Extend success!');
                this.loadingConfirmExtendOTA = false;
                this.actionHideConfirmExtendOTAModal();
            } catch (error) {
                this.loadingConfirmExtendOTA = false;
                console.error("Gagal mengonfirmasi extend:", error);
                this.$alert.toast('error', error.response?.data?.msg || 'Extend failed!');
            }
        },
        async applyVoucherExtendAction() {
            if (this.isVoucherApplying) return;
            this.isVoucherApplying = true;
            try {
                const req = {
                    BookingCode: this.bookingDetail?.Code ?? null,
                    BookDetailId: this.extendData?.Id ?? null,
                    VoucherCode: this.voucherCode,
                };
                const res = await this.applyVoucherExtend(req);
                if (res.data) {
                    this.extendData = res.data;
                    this.voucherCode = '';
                }
                this.$alert.toast('success', 'Voucher applied!');
                this.isVoucherApplying = false;
            } catch (error) {
                this.isVoucherApplying = false;
                this.$alert.toast('error', error.response?.data?.msg ?? 'Failed, please try again!');
                console.error("Gagal mengaplikasikan voucher:", error);
            }
        },
        async removeVoucherExtendAction(item) {
            if (this.isVoucherApplying) return;
            this.isVoucherApplying = true;
            try {
                const req = {
                    BookingCode: this.bookingDetail?.Code ?? null,
                    BookDetailId: this.extendData?.Id ?? null,
                    VoucherCode: item.VoucherCode,
                };
                const res = await this.removeVoucherExtend(req);
                if (res.data) {
                    this.extendData = res.data;
                }
                this.$alert.toast('success', 'Voucher removed!');
                this.isVoucherApplying = false;
            } catch (error) {
                this.isVoucherApplying = false;
                this.$alert.toast('error', error.response?.data?.msg ?? 'Failed, please try again!');
            }
        },
        async downloadSelectedInvoice() {
            if (this.selectedInvoice) {
                return;
            }
            try {
                if (!this.selectedInvoice.BookingCode || typeof this.selectedInvoice.BookingCode !== "string") {
                    this.$alert.toast("error", "Booking code is invalid.");
                    return;
                }
                await this.downloadReceipt({ bookingCode: this.selectedInvoice.BookingCode });
            } catch (error) {
                console.error("Failed to download receipt:", error);
                this.$alert.toast("error", this.$t("alert.failedToFetchReceipt"));
            } finally {
                this.selectedInvoice = null;
                this.showDownloadInvoiceModal = null;
            }
        },
    },
    async mounted() {
        const keyFromQuery = this.$route.query.key;
        if (keyFromQuery) {
            this.queryKey = keyFromQuery;
            console.log("Query Key Found:", this.queryKey, this.bookingCode);
        }

        if (this.bookingCode) {
            await this.fetchBookingDetail();

            // this.fetchArrayCleaningRequestsTemp();
            // Jangan panggil loadCleaningRequests jika queryKey ada
            // if (!this.queryKey && this.bookingDetail?.BookDetails?.length) {
            //     for (const detail of this.bookingDetail.BookDetails) {
            //         await this.loadCleaningRequests(detail.Id);
            //     }
            // }
        } else {
            console.error("Booking code not provided.");
        }

        console.log("bookingDetail", this.bookingDetail);

    }
};
</script>

<style scoped>
.modal-container {
    z-index: 1000;
}

.modal-wrapper {
    max-height: 83vh;
    /* Membatasi tinggi modal */
    overflow-y: auto;
    /* Menambahkan scrollbar jika konten melebihi tinggi modal */
}

.modal-content {
    max-width: 95vw;
    overflow-y: auto;
    /* Membatasi lebar modal */
    overflow-x: hidden;
    /* Mencegah overflow horizontal */
    box-sizing: border-box;
    /* Menghindari masalah padding yang melampaui batas */
    padding: 16px;
}
</style>
